import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject, NgZone } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalRef, NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzUploadModule } from 'ng-zorro-antd/upload';
import type { NzUploadFile, NzUploadXHRArgs } from 'ng-zorro-antd/upload';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { HttpService } from 'src/app/http/http.service';
import { RobotAnswerTestModalComponent } from 'src/app/shared/components/robot-answer-test-modal/robot-answer-test-modal.component';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

type WebsiteTaskStatus = 'queued' | 'running' | 'completed' | 'completed_with_warnings' | 'failed' | 'unknown';
type AttachmentSourceType = 'website' | 'file' | 'youtube_link' | 'plain_text';

interface WebsiteItem {
  url: string;
  remark?: string;
  taskId?: string;
  taskStatus?: WebsiteTaskStatus;
  statusText?: string;
  subStatusText?: string;
  stage?: string;
  latestMessage?: string;
  progressPercent?: number;
  timeoutHintVisible?: boolean;
}

interface AttachmentTask {
  taskId: string;
  sourceType: AttachmentSourceType;
  status: WebsiteTaskStatus;
  stage?: string;
  url?: string;
  latestMessage?: string;
  progressPercent?: number;
  totalCount?: number;
  processedCount?: number;
  successCount?: number;
  failedCount?: number;
  skippedCount?: number;
  warningCount?: number;
  lastError?: string;
  lookupFailures?: number;
  retainUntil?: number;
}

interface AssetTaskDisplay {
  taskId?: string;
  taskStatus?: WebsiteTaskStatus;
  stage?: string;
  latestMessage?: string;
  progressPercent?: number;
}

interface RoleSettingsFormValue {
  persona: string;
  background: string;
  tone: string;
  contentSize: string;
  welcomeMessage: string;
  thanksMessage: string;
  noAnswerMessage: string;
  userTitle: string;
  serviceTitle: string;
  answerStyle: string;
}

@Component({
  selector: 'robot-service-config',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzSelectModule,
    NzCardModule,
    NzGridModule,
    NzIconModule,
    NzTabsModule,
    NzUploadModule,
    NzSpinModule,
    NzModalModule,
    NzProgressModule,
    NzTagModule,
    NzToolTipModule,
    TestIdDirective,
    I18nPipe
  ],
  templateUrl: './robot-service-config.component.html',
  styleUrls: ['./robot-service-config.component.less']
})
export class RobotServiceConfigComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly modalRef = inject(NzModalRef, { optional: true });
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly message = inject(NzMessageService);
  private readonly modalService = inject(NzModalService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly http = inject(HttpService);
  private readonly httpClient = inject(HttpClient);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly ngZone = inject(NgZone);
  private readonly modalData: any = inject(NZ_MODAL_DATA, { optional: true }) || {};

  configForm!: FormGroup;
  uploadDisabled = true;
  // 頁面整體加載狀態（用於顯示加載動畫覆蓋層）
  pageLoading = true;
  assetDeleteLoading = false;
  roleSaving = false;
  // 操作區域：暫時隱藏保存按鈕，禁用重新載入
  showSaveButton = false;
  reloadDisabled = false;
  // 角色設定已開放，與知識管理區分開處理
  showRole = true;
  robotId: string | null = null;
  serviceNumberId: string | null = null;
  @Input() tenantIdInput: string | null = null;
  @Input() serviceNumberIdInput: string | null = null;
  @Input() robotIdInput: string | null = null;
  @Input() embeddedInDrawer = false;
  @Output() readonly closeRequested = new EventEmitter<void>();

  // 已上傳文件列表（臨時存放於前端，後端接好 API 後可替換為實際數據）
  uploadedFiles: Array<
    {
      id?: string;
      name: string;
      size: number;
      uploadedAt: Date;
      fileId?: string;
      accessPath?: string;
      accessUrl?: string;
    } & AssetTaskDisplay
  > = [];
  uploading = false;
  // Websites management
  websites: WebsiteItem[] = [];
  newWebsiteUrl: string = '';
  newWebsiteRemark: string = '';
  // Website validation & preview modal state
  websiteValidationVisible = false;
  websitePreviewUrl?: SafeResourceUrl;
  websiteValidationLoading = false;
  websiteValidationError: string | null = null;
  websitePreviewAvailable = false;
  // Website OG metadata fallback
  websiteOgImageUrl: string | null = null;
  websiteOgTitle: string | null = null;
  private websiteValidationTimeoutId: any;
  private websiteProgressTimeoutIds = new Map<string, any>();
  private websiteProgressInFlightTaskIds = new Set<string>();
  private removedAttachmentTaskIds = new Set<string>();
  private requestInFlightKeys = new Set<string>();
  attachmentTasks: AttachmentTask[] = [];
  websiteSetSubmitting = false;
  private readonly websiteProgressPollIntervalMs = 5000;
  private readonly attachmentTaskRetentionMs = 10000;

  // YouTube links management
  youtubeContents: Array<{ youtubeLink: string; remark?: string; thumbnailUrl?: string } & AssetTaskDisplay> = [];
  newYoutubeLink: string = '';
  newYoutubeRemark: string = '';
  // YouTube validation modal state
  youtubeValidationVisible = false;
  youtubeValidationLoading = false;
  youtubeValidationError: string | null = null;
  youtubeVideoTitle: string | null = null;
  youtubeVideoId: string | null = null;
  youtubeDurationSec: number | null = null;
  youtubeHasCaptions: boolean | null = null;
  // YouTube 校驗成功後的預覽縮略圖（方案A）
  youtubeThumbnailUrl: string | null = null;
  private youtubeValidationTimeoutId: any;
  private ytPlayer: any = null;
  private ytApiReadyPromise: Promise<void> | null = null;
  private ytApiLoadFailed = false;
  // Plain text contents display
  plainTextContents: Array<{ plainText: string; remark?: string } & AssetTaskDisplay> = [];
  newPlainTextContent: string = '';

  // 知識容量使用（僅計算文件與文本），總容量固定 40MB
  knowledgeTotalBytes = 40 * 1024 * 1024;
  knowledgeUsedBytes = 0;
  knowledgeUsagePercent = 0;
  knowledgeUsageFormat = (_percent?: number) =>
    `${this.formatFileSize(this.knowledgeUsedBytes)} / ${this.formatFileSize(this.knowledgeTotalBytes)}`;

  private t(key: string, params?: Record<string, unknown>): string {
    return this.i18n.fanyi(key, params);
  }

  private runViewUpdate(update: () => void): void {
    const zone = this.ngZone;
    if (zone?.run) {
      zone.run(update);
      return;
    }
    update();
  }

  private beginPageRequest(key: string): boolean {
    if (!this.requestInFlightKeys) {
      this.requestInFlightKeys = new Set<string>();
    }
    if (this.requestInFlightKeys.has(key)) {
      return false;
    }
    this.requestInFlightKeys.add(key);
    return true;
  }

  private endPageRequest(key: string): void {
    this.requestInFlightKeys?.delete(key);
  }

  private beginAssetDeleteRequest(key: string): boolean {
    if (this.assetDeleteLoading) return false;
    if (!this.beginPageRequest(key)) return false;
    this.assetDeleteLoading = true;
    return true;
  }

  private endAssetDeleteRequest(key: string): void {
    this.endPageRequest(key);
    this.assetDeleteLoading = false;
  }

  openRobotAnswerTest(): void {
    if (!this.robotId || !this.serviceNumberId) {
      return;
    }

    this.modalService.create({
      nzClassName: 'robot-answer-test-modal-shell',
      nzTitle: '',
      nzContent: RobotAnswerTestModalComponent,
      nzWidth: '1100px',
      nzFooter: null,
      nzData: {
        serviceNumberId: this.serviceNumberId,
        robotId: this.robotId,
        robotName: this.resolveRobotDisplayName()
      }
    });
  }

  private resolveRobotDisplayName(): string {
    const formValue = this.configForm?.get?.('robotName')?.value;
    return String(formValue ?? this.modalData?.robotName ?? this.robotId ?? '').trim();
  }

  ngOnInit(): void {
    this.initializeState();
  }

  ngOnDestroy(): void {
    this.clearWebsiteProgressPolling();
    if (this.websiteValidationTimeoutId) {
      clearTimeout(this.websiteValidationTimeoutId);
    }
    if (this.youtubeValidationTimeoutId) {
      clearTimeout(this.youtubeValidationTimeoutId);
    }
  }

  // 將頁面載入的初始化邏輯抽取，供 ngOnInit 與重新載入共用
  private initializeState(): void {
    // 進入初始化即開始顯示加載動畫
    this.pageLoading = true;
    this.showRole = true;
    this.embeddedInDrawer = this.embeddedInDrawer || !!this.modalData?.embeddedInDrawer;
    // 讀取查詢參數中的 robotId 與 serviceNumberId
    this.robotId = this.robotIdInput || this.modalData?.robotId || this.route.snapshot.queryParamMap.get('robotId');
    this.serviceNumberId =
      this.serviceNumberIdInput || this.modalData?.serviceNumberId || this.route.snapshot.queryParamMap.get('serviceNumberId');
    const tenantId =
      this.tenantIdInput ||
      this.modalData?.tenantId ||
      this.route.snapshot.paramMap.get('id') ||
      this.route.parent?.snapshot.paramMap.get('tenantId');
    // 根據參數是否齊全控制上傳可用性
    this.uploadDisabled = !(this.robotId && this.serviceNumberId);
    let restoredAttachmentTasks = false;
    if (!this.uploadDisabled) {
      this.restoreAttachmentTasks();
      restoredAttachmentTasks = (this.attachmentTasks?.length ?? 0) > 0;
      this.resumeAttachmentTaskPolling();
    }
    // 若參數缺失，無法拉取數據，結束加載
    if (this.uploadDisabled) {
      this.pageLoading = false;
    }
    // 初始化表單（與頁面初次載入一致）
    this.configForm = this.buildRoleSettingsForm();

    if (this.serviceNumberId && !restoredAttachmentTasks) {
      this.loadRoleSettings();
    }

    // 參數齊全時載入已有資產列表
    if (!this.uploadDisabled) {
      this.loadAssetsList();
    }
  }

  reload(): void {
    this.loadRoleSettings();
    if (!this.uploadDisabled) {
      this.loadAssetsList();
    }
  }

  async submitRoleSettings(): Promise<void> {
    await this.saveRoleSettings();
  }

  private buildRoleSettingsForm(): FormGroup {
    const defaults = this.getRecommendedRoleSettingsDefaults();
    return this.fb.group({
      persona: [defaults.persona],
      background: [defaults.background],
      tone: [defaults.tone],
      contentSize: [defaults.contentSize],
      welcomeMessage: [defaults.welcomeMessage],
      thanksMessage: [defaults.thanksMessage],
      noAnswerMessage: [defaults.noAnswerMessage],
      userTitle: [defaults.userTitle],
      serviceTitle: [defaults.serviceTitle],
      answerStyle: [defaults.answerStyle],
      youtubeUrl: [null]
    });
  }

  private getRecommendedRoleSettingsDefaults(): RoleSettingsFormValue {
    return {
      persona: 'assistant',
      background: 'none',
      tone: 'friendly',
      contentSize: 'medium',
      welcomeMessage: '',
      thanksMessage: '',
      noAnswerMessage: '',
      userTitle: '',
      serviceTitle: '',
      answerStyle: ''
    };
  }

  private loadRoleSettings(): void {
    if (!this.serviceNumberId) {
      return;
    }
    this.http.post(`${ConstantUtil.ADMIN_SERVICENUMBER_ROBOT_PROMPT_CONFIG_DETAIL}/${this.serviceNumberId}`, {}).subscribe({
      next: res => {
        if (res?.success !== true) {
          return;
        }
        const detail = this.mapRobotRoleDetailToFormValue(this.extractPromptConfigDetail(res?.data));
        this.configForm.patchValue(detail, { emitEvent: false });
        this.configForm.markAsPristine();
      },
      error: () => {}
    });
  }

  private extractPromptConfigDetail(payload: any): any {
    if (!payload || typeof payload !== 'object') {
      return payload;
    }
    return payload?.promptConfig ?? payload?.config ?? payload;
  }

  private mapRobotRoleDetailToFormValue(detail: any): RoleSettingsFormValue {
    const defaults = this.getRecommendedRoleSettingsDefaults();
    return {
      persona: this.pickFirstNonEmpty(detail?.persona, detail?.rolePersona, detail?.robotPersona, defaults.persona),
      background: this.pickFirstNonEmpty(detail?.background, detail?.chatBackground, defaults.background),
      tone: this.pickFirstNonEmpty(detail?.tone, detail?.replyTone, defaults.tone),
      contentSize: this.pickFirstNonEmpty(detail?.contentSize, detail?.replyLength, defaults.contentSize),
      welcomeMessage: this.pickFirstNonEmpty(detail?.welcomeMessage, detail?.greetingText, defaults.welcomeMessage),
      thanksMessage: this.pickFirstNonEmpty(detail?.thanksMessage, detail?.thankYouText, detail?.thanksText, defaults.thanksMessage),
      noAnswerMessage: this.pickFirstNonEmpty(
        detail?.noAnswerMessage,
        detail?.fallbackReplyText,
        detail?.noAnswerText,
        defaults.noAnswerMessage
      ),
      userTitle: this.pickFirstNonEmpty(detail?.userTitle, detail?.userAlias, detail?.domainTerms?.userLabel, defaults.userTitle),
      serviceTitle: this.pickFirstNonEmpty(
        detail?.serviceTitle,
        detail?.serviceAlias,
        detail?.domainTerms?.serviceLabel,
        defaults.serviceTitle
      ),
      answerStyle: this.pickFirstNonEmpty(detail?.answerStyle, detail?.replyStyle, defaults.answerStyle)
    };
  }

  private pickFirstNonEmpty(...values: unknown[]): string {
    for (const value of values) {
      if (value === null || value === undefined) continue;
      const text = String(value);
      if (text !== '') {
        return text;
      }
    }
    return '';
  }

  private resetRoleSettingsToDefaults(): void {
    const defaults = this.getRecommendedRoleSettingsDefaults();
    this.configForm.patchValue(defaults, { emitEvent: false });
    this.configForm.markAsPristine();
  }

  private buildRoleSettingsSavePayload(): Record<string, any> {
    const formValue = this.configForm.getRawValue();
    return {
      serviceNumberId: this.serviceNumberId,
      promptConfig: {
        schemaVersion: 1,
        persona: this.pickFirstNonEmpty(formValue?.persona),
        background: this.pickFirstNonEmpty(formValue?.background),
        tone: this.pickFirstNonEmpty(formValue?.tone),
        contentSize: this.pickFirstNonEmpty(formValue?.contentSize),
        greetingText: String(formValue?.welcomeMessage ?? ''),
        thanksText: String(formValue?.thanksMessage ?? ''),
        noAnswerText: String(formValue?.noAnswerMessage ?? ''),
        answerStyle: String(formValue?.answerStyle ?? ''),
        domainTerms: {
          userLabel: String(formValue?.userTitle ?? ''),
          serviceLabel: String(formValue?.serviceTitle ?? '')
        }
      }
    };
  }

  private saveRoleSettings(): Promise<boolean> {
    if (!this.robotId || !this.serviceNumberId) {
      this.message.warning(this.t('robot-service.config.error.missing-params'));
      return Promise.resolve(false);
    }
    this.roleSaving = true;
    return new Promise(resolve => {
      this.http
        .post(ConstantUtil.ADMIN_SERVICENUMBER_ROBOT_PROMPT_CONFIG_UPDATE, this.buildRoleSettingsSavePayload())
        .pipe(finalize(() => (this.roleSaving = false)))
        .subscribe({
          next: res => {
            if (res?.success === true) {
              this.message.success(this.t('robot-service.config.role.save-success'));
              this.configForm.markAsPristine();
              resolve(true);
              return;
            }
            this.message.error(res?.msg || this.t('robot-service.config.role.save-failed'));
            resolve(false);
          },
          error: () => {
            this.message.error(this.t('robot-service.config.role.save-failed'));
            resolve(false);
          }
        });
    });
  }

  // 自訂上傳：將 robotId、serviceNumberId 一併傳入，並在成功後刷新列表
  customUploadReq = (item: NzUploadXHRArgs): Subscription => {
    if (!this.robotId || !this.serviceNumberId) {
      this.message.warning(this.t('robot-service.config.error.missing-params'));
      item.onError?.('missing params', item.file);
      return new Subscription();
    }
    const nzFile = item.file as NzUploadFile;
    const rawFile: File = (nzFile.originFileObj as File) || (nzFile as unknown as File);
    // 新增 contentType 參數：為當前上傳文件的 MIME 類型
    // 若後續改為多文件同時上傳，可將多個類型以逗號分隔組合
    const singleContentType = rawFile?.type || 'application/octet-stream';
    const args = {
      robotId: this.robotId,
      serviceNumberId: this.serviceNumberId,
      contentType: singleContentType
    };
    const requestKey = 'assets:upload';
    if (!this.beginPageRequest(requestKey)) {
      item.onError?.('request in progress', nzFile);
      return new Subscription();
    }
    this.uploading = true;
    // 使用資產上傳端點
    const sub = this.http
      .postFormData(ConstantUtil.ADMIN_SERVICENUMBER_ROBOT_ASSETS_UPLOAD, args, rawFile, 'files')
      .pipe(finalize(() => this.endPageRequest(requestKey)))
      .subscribe({
        next: res => {
          this.uploading = false;
          if (res && res.success) {
            const data = this.getAssetSetPayload(res?.data);
            const displayMessage =
              typeof data?.displayMessage === 'string' && data.displayMessage.trim()
                ? data.displayMessage
                : this.t('robot-service.config.upload.success');
            this.message.success(displayMessage);
            if (this.shouldTrackAssetSetTask(data, 'file')) {
              this.trackAttachmentTaskFromSetPayload(data, 'file', rawFile?.name || '', displayMessage);
            } else {
              // 舊接口成功後刷新列表，確保與後端數據一致。
              this.loadAssetsList();
            }
            item.onSuccess?.(res, nzFile, undefined);
          } else {
            this.message.error(res?.msg || this.t('robot-service.config.upload.failed'));
            item.onError?.(res?.msg || 'upload failed', nzFile);
          }
        },
        error: (err: any) => {
          this.uploading = false;
          this.message.error(this.t('robot-service.config.upload.failed'));
          item.onError?.(err, nzFile);
        }
      });
    return sub;
  };

  // 載入資產文件列表與各類內容
  loadAssetsList(): void {
    if (!this.robotId || !this.serviceNumberId) return;
    const requestKey = 'assets:list';
    if (!this.beginPageRequest(requestKey)) return;
    // 重新拉取列表時顯示加載動畫
    this.pageLoading = true;
    const body = { robotId: this.robotId, serviceNumberId: this.serviceNumberId };
    this.http
      .post(ConstantUtil.ADMIN_SERVICENUMBER_ROBOT_ASSETS, body)
      .pipe(finalize(() => this.endPageRequest(requestKey)))
      .subscribe(res => {
        if (res?.success) {
          // 新結構可能返回多個服務號的資料，僅映射當前服務號，避免知識內容跨機器人顯示。
          const hasAssetsPayload = Array.isArray(res?.data?.assets);
          const scopedAssets: any[] = hasAssetsPayload
            ? res.data.assets.filter((asset: any) => String(asset?.serviceId ?? '').trim() === String(this.serviceNumberId).trim())
            : [];

          // 上傳文件（聚合）
          this.uploadedFiles = scopedAssets.flatMap(asset => {
            const uploadFiles: any[] = Array.isArray(asset?.uploadFiles) ? asset.uploadFiles : [];
            return uploadFiles.map(f => ({
              id: f?.id ?? null,
              fileId: f?.fileId ?? null,
              name: f?.name ?? f?.fileName ?? 'Unknown',
              size: f?.size ?? 0,
              uploadedAt: new Date(asset?.updateAt ?? asset?.createAt ?? Date.now()),
              accessPath: f?.accessPath ?? f?.downloadPath ?? f?.filePath ?? f?.path ?? null,
              accessUrl: f?.accessUrl ?? f?.downloadUrl ?? null,
              taskId: this.extractAssetTaskId(f),
              taskStatus: this.normalizeWebsiteTaskStatus(f?.status ?? f?.taskStatus),
              stage: f?.stage ?? undefined,
              latestMessage: f?.latestMessage ?? f?.latest_message ?? undefined,
              progressPercent: this.normalizeProgressPercent(f?.progressPercent ?? f?.progress_percent)
            }));
          });

          // 網址內容（聚合）
          this.websites = scopedAssets.flatMap(asset => {
            const urlContents: any[] = Array.isArray(asset?.urlContents) ? asset.urlContents : [];
            return urlContents.map(u => ({
              url: String(u?.websiteUrl ?? '')
                .replace(/`/g, '')
                .trim(),
              remark: u?.remark ?? '',
              taskId: this.extractAssetTaskId(u),
              taskStatus: this.normalizeWebsiteTaskStatus(u?.status ?? u?.taskStatus),
              stage: u?.stage ?? undefined,
              latestMessage: u?.latestMessage ?? undefined,
              progressPercent: this.normalizeProgressPercent(u?.progressPercent),
              ...this.getWebsiteStatusDisplay(this.normalizeWebsiteTaskStatus(u?.status ?? u?.taskStatus))
            }));
          });

          // YouTube 內容（聚合）
          this.youtubeContents = scopedAssets.flatMap(asset => {
            const youtubeContents: any[] = Array.isArray(asset?.youtubeContents) ? asset.youtubeContents : [];
            return youtubeContents.map(y => ({
              youtubeLink: String(y?.youtubeLink ?? '')
                .replace(/`/g, '')
                .trim(),
              remark: y?.remark ?? '',
              thumbnailUrl: undefined,
              taskId: this.extractAssetTaskId(y),
              taskStatus: this.normalizeWebsiteTaskStatus(y?.status ?? y?.taskStatus),
              stage: y?.stage ?? undefined,
              latestMessage: y?.latestMessage ?? y?.latest_message ?? undefined,
              progressPercent: this.normalizeProgressPercent(y?.progressPercent ?? y?.progress_percent)
            }));
          });

          // 純文本內容（聚合）
          this.plainTextContents = scopedAssets.flatMap(asset => {
            const plainTextContents: any[] = Array.isArray(asset?.plainTextContents) ? asset.plainTextContents : [];
            return plainTextContents.map(p => ({
              plainText: p?.plainText ?? '',
              remark: p?.remark ?? '',
              taskId: this.extractAssetTaskId(p),
              taskStatus: this.normalizeWebsiteTaskStatus(p?.status ?? p?.taskStatus),
              stage: p?.stage ?? undefined,
              latestMessage: p?.latestMessage ?? p?.latest_message ?? undefined,
              progressPercent: this.normalizeProgressPercent(p?.progressPercent ?? p?.progress_percent)
            }));
          });

          // 兼容舊結構：若 data.assets 不存在，則回退舊列表處理
          if (!hasAssetsPayload) {
            const rawList: any[] = Array.isArray(res?.data?.list) ? res.data.list : Array.isArray(res?.data) ? (res.data as any[]) : [];
            this.uploadedFiles = rawList.map(item => ({
              id: item?.id ?? item?.fileId ?? item?.attachmentId ?? item?.uid ?? null,
              fileId: item?.fileId ?? item?.id ?? null,
              name: item?.name ?? item?.fileName ?? item?.filename ?? 'Unknown',
              size: item?.size ?? item?.length ?? item?.fileSize ?? 0,
              uploadedAt: new Date(item?.uploadedAt ?? item?.createTime ?? item?.createdAt ?? Date.now()),
              accessPath: item?.accessPath ?? item?.downloadPath ?? item?.filePath ?? item?.path ?? null,
              accessUrl: item?.accessUrl ?? item?.downloadUrl ?? null,
              taskId: this.extractAssetTaskId(item),
              taskStatus: this.normalizeWebsiteTaskStatus(item?.status ?? item?.taskStatus),
              stage: item?.stage ?? undefined,
              latestMessage: item?.latestMessage ?? item?.latest_message ?? undefined,
              progressPercent: this.normalizeProgressPercent(item?.progressPercent ?? item?.progress_percent)
            }));
          }
          this.mergeActiveAttachmentTasksIntoSourceLists();
          // 為列表中的 YouTube 條目加載縮略圖（方案A）
          this.attachYoutubeThumbnails();

          // 更新容量使用情況（文件 + 文本）
          this.calculateKnowledgeUsage();
          // 數據與容量計算完成，關閉加載動畫
          this.pageLoading = false;
        } else {
          // 調用失敗亦需關閉加載動畫避免卡死
          this.pageLoading = false;
        }
      });
  }

  // 從後端刪除文件（按文件名）並更新列表
  deleteFile(file: any, index?: number): void {
    if (!this.robotId || !this.serviceNumberId) return;
    const fileName = file?.name ?? file?.fileName;
    if (!fileName) {
      this.message.warning(this.t('robot-service.config.file-name-missing'));
      return;
    }
    const body = { robotId: this.robotId, serviceNumberId: this.serviceNumberId, fileNames: [fileName] };
    const requestKey = `assets:file:delete:${fileName}`;
    if (!this.beginAssetDeleteRequest(requestKey)) return;
    this.http
      .post(ConstantUtil.ADMIN_SERVICENUMBER_ROBOT_ASSETS_DELETE, body)
      .pipe(finalize(() => this.endAssetDeleteRequest(requestKey)))
      .subscribe(res => {
        if (res?.success) {
          this.removeAttachmentTaskBySource('file', task => {
            const taskFileName = String(task.url || '').trim();
            return task.taskId === file?.taskId || (!!taskFileName && taskFileName === fileName);
          });
          if (typeof index === 'number') {
            this.uploadedFiles.splice(index, 1);
          } else {
            this.uploadedFiles = this.uploadedFiles.filter(f => f.name !== fileName);
          }
          this.calculateKnowledgeUsage();
          this.message.success(this.t('robot-service.config.delete-success'));
        } else {
          this.message.error(res?.msg || this.t('robot-service.config.delete-failed'));
        }
      });
  }
  downloadFile(file: any): void {
    if (!this.robotId || !this.serviceNumberId) {
      this.message.warning(this.t('robot-service.config.error.missing-params'));
      return;
    }
    const payload = this.buildFileDownloadPayload(file);
    if (!payload) {
      this.message.warning(this.t('robot-service.config.download-path-missing'));
      return;
    }

    // 使用 HttpService.postBlob：傳入 API 路徑（避免重複拼接 baseUrl）
    const requestKey = `assets:file:download:${payload.filePath}`;
    if (!this.beginPageRequest(requestKey)) return;
    this.http
      .postBlob(ConstantUtil.ADMIN_SERVICENUMBER_ROBOT_ASSETS_DOWNLOAD, payload)
      .pipe(finalize(() => this.endPageRequest(requestKey)))
      .subscribe({
        next: (blob: Blob) => {
          try {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = String(file?.name || 'attachment');
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 500);
            this.message.success(this.t('robot-service.config.download.start'));
          } catch (e) {
            this.message.error(this.t('robot-service.config.download.failed'));
          }
        },
        error: err => {
          console.error('[downloadFile] 請求錯誤（postBlob）', err);
          this.message.error(this.t('robot-service.config.download.failed'));
        }
      });
  }

  shouldShowFileDownloadButton(file: { accessPath?: string; accessUrl?: string } | null | undefined): boolean {
    return this.hasFileDownloadSource(file);
  }

  // Websites: save via SET API
  saveWebsites(): void {
    if (this.websiteSetSubmitting) {
      return;
    }
    if (!this.robotId || !this.serviceNumberId) {
      this.message.warning(this.t('robot-service.config.error.missing-params'));
      return;
    }
    const url = (this.newWebsiteUrl || '').trim();
    const remark = (this.newWebsiteRemark || '').trim();
    if (!url) {
      this.message.warning(this.t('robot-service.config.websites.required'));
      return;
    }
    if (!this.isValidUrl(url)) {
      this.message.error(this.t('robot-service.config.websites.invalid'));
      return;
    }
    const requestKey = 'assets:website:set';
    if (!this.beginPageRequest(requestKey)) return;
    this.websiteSetSubmitting = true;
    const body = {
      serviceNumberId: this.serviceNumberId,
      robotId: this.robotId,
      // 後端僅接受單一項
      webSites: [{ url, remark }]
    };
    this.http
      .post(ConstantUtil.ADMIN_SERVICENUMBER_ROBOT_ASSETS_WEBSITE_SET, body)
      .pipe(
        finalize(() => {
          this.websiteSetSubmitting = false;
          this.endPageRequest(requestKey);
        })
      )
      .subscribe(res => {
        if (this.isWebsiteSetSuccess(res)) {
          const data = this.getWebsiteSetPayload(res?.data);
          const taskId = this.extractAssetTaskId(data);
          const displayMessage =
            typeof data?.displayMessage === 'string' && data.displayMessage.trim()
              ? data.displayMessage
              : taskId
                ? this.t('robot-service.config.websites.processing-success')
                : this.t('robot-service.config.save-success');
          this.message.success(displayMessage);
          // 若當前存在校驗預覽彈窗，保存成功後自動關閉
          if (this.websiteValidationVisible) {
            this.websiteValidationVisible = false;
          }
          if (this.isWebsiteSetTaskPayload(data)) {
            this.upsertWebsitePendingItem(url, remark, taskId, displayMessage);
            this.trackAttachmentTaskFromSetPayload(data, 'website', url, displayMessage);
          } else {
            // 舊接口布爾成功時刷新列表，維持既有行為。
            this.loadAssetsList();
          }
        } else {
          this.message.error(res?.msg || this.t('robot-service.config.save-failed'));
        }
      });
  }

  private isWebsiteSetSuccess(res: any): boolean {
    return this.isAssetSetSuccess(res);
  }

  private isAssetSetSuccess(res: any): boolean {
    if (res?.success !== true) return false;
    const data = this.getAssetSetPayload(res?.data);
    return data === true || data === undefined || data?.code === 0;
  }

  private getWebsiteSetPayload(data: any): any {
    return this.getAssetSetPayload(data);
  }

  private getAssetSetPayload(data: any): any {
    if (data && typeof data === 'object' && data.data && typeof data.data === 'object') {
      return data.data;
    }
    return data;
  }

  private isWebsiteSetTaskPayload(data: any): boolean {
    return this.isAssetSetTaskPayload(data, 'website');
  }

  private isAssetSetTaskPayload(data: any, sourceType?: AttachmentSourceType): boolean {
    if (!data || typeof data !== 'object') return false;
    const normalizedSourceType = this.normalizeAttachmentSourceType(data.source_type ?? data.sourceType);
    return (
      data.code === 0 || normalizedSourceType === sourceType || typeof data.displayMessage === 'string' || !!this.extractAssetTaskId(data)
    );
  }

  private shouldTrackAssetSetTask(data: any, sourceType: AttachmentSourceType): boolean {
    return !!this.extractAssetTaskId(data) && this.isAssetSetTaskPayload(data, sourceType);
  }

  private extractWebsiteTaskId(data: any): string | undefined {
    return this.extractAssetTaskId(data);
  }

  private extractAssetTaskId(data: any): string | undefined {
    if (!data || typeof data !== 'object') return undefined;
    const value = data.taskId ?? data.taskID ?? data.task_id ?? data.id ?? data.task?.id ?? data.task?.taskId;
    return value === undefined || value === null || value === '' ? undefined : String(value);
  }

  private trackAttachmentTaskFromSetPayload(
    data: any,
    sourceType: AttachmentSourceType,
    url = '',
    fallbackMessage?: string
  ): string | undefined {
    const taskId = this.extractAssetTaskId(data);
    if (!taskId) return undefined;
    const latestMessage =
      typeof data?.displayMessage === 'string' && data.displayMessage.trim()
        ? data.displayMessage
        : fallbackMessage || this.t('robot-service.config.websites.processing-success');
    this.ensureRemovedAttachmentTaskIds();
    this.removedAttachmentTaskIds.delete(taskId);
    this.upsertAttachmentTask({
      taskId,
      sourceType,
      status: 'queued',
      stage: 'queued',
      url,
      latestMessage,
      progressPercent: undefined
    });
    this.pollWebsiteAssetProgress(taskId, url, 0);
    return taskId;
  }

  private upsertWebsitePendingItem(url: string, remark: string, taskId?: string, latestMessage?: string): void {
    const item: WebsiteItem = {
      url,
      remark,
      taskId,
      taskStatus: 'queued',
      ...this.getWebsiteStatusDisplay('queued'),
      progressPercent: 0,
      latestMessage: latestMessage || this.t('robot-service.config.websites.processing-success')
    };
    const index = this.websites.findIndex(w => w.url === url);
    if (index >= 0) {
      this.websites[index] = { ...this.websites[index], ...item };
    } else {
      this.websites = [...this.websites, item];
    }
  }

  private scheduleWebsiteProgressPoll(taskId: string, url: string, attempt = 0): void {
    this.ensureWebsiteProgressTimeoutMap();
    this.clearWebsiteProgressPolling(taskId);
    const timeoutId = setTimeout(() => this.pollWebsiteAssetProgress(taskId, url, attempt), this.websiteProgressPollIntervalMs ?? 5000);
    this.websiteProgressTimeoutIds.set(taskId, timeoutId);
  }

  private pollWebsiteAssetProgress(taskId: string, url: string, attempt: number): void {
    if (!taskId) return;
    if (!this.robotId || !this.serviceNumberId) return;
    if (this.isAttachmentTaskRemoved(taskId)) {
      this.clearWebsiteProgressPolling(taskId);
      return;
    }
    const requestKey = taskId;
    if (!this.websiteProgressInFlightTaskIds) {
      this.websiteProgressInFlightTaskIds = new Set<string>();
    }
    if (this.websiteProgressInFlightTaskIds.has(requestKey)) return;
    this.websiteProgressInFlightTaskIds.add(requestKey);
    const body = {
      serviceNumberId: this.serviceNumberId,
      robotId: this.robotId,
      taskId,
      limit: 10
    };
    const progressRequest = this.http.post(ConstantUtil.ADMIN_SERVICENUMBER_ROBOT_ASSETS_PROGRESS, body);
    if (!progressRequest?.pipe) {
      this.websiteProgressInFlightTaskIds.delete(requestKey);
      this.clearWebsiteProgressPolling(taskId);
      return;
    }
    progressRequest.pipe(finalize(() => this.websiteProgressInFlightTaskIds.delete(requestKey))).subscribe({
      next: res => {
        this.runViewUpdate(() => {
          if (this.isAttachmentTaskRemoved(taskId)) {
            this.clearWebsiteProgressPolling(taskId);
            return;
          }
          if (res?.success !== true) {
            this.applyWebsiteProgress(url, { taskStatus: 'unknown', latestMessage: res?.msg });
            if (taskId) {
              this.markAttachmentTaskFailed(taskId, res?.msg || this.t('robot-service.config.websites.progress-failed'));
            }
            return;
          }
          const source = this.pickWebsiteProgressSource(res?.data, taskId);
          const resolvedTaskId = taskId || this.extractWebsiteTaskId(source) || '';
          const progress = this.normalizeWebsiteProgress(res?.data, resolvedTaskId || taskId);
          if (resolvedTaskId && resolvedTaskId !== taskId) {
            this.applyWebsiteProgress(url, { taskId: resolvedTaskId });
          }
          if (resolvedTaskId) {
            const task = this.normalizeAttachmentTaskFromProgress(res?.data, resolvedTaskId, url);
            this.applySourceProgress(task);
            this.upsertAttachmentTask(task);
          } else {
            this.applyWebsiteProgress(url, progress);
          }
          if (resolvedTaskId && (progress.taskStatus === 'queued' || progress.taskStatus === 'running')) {
            this.scheduleWebsiteProgressPoll(resolvedTaskId, url, attempt + 1);
            return;
          }
          this.clearWebsiteProgressPolling(resolvedTaskId || taskId);
          if (progress.taskStatus === 'completed' || progress.taskStatus === 'completed_with_warnings') {
            this.loadAssetsList();
          }
        });
      },
      error: () => {
        this.runViewUpdate(() => {
          if (this.isAttachmentTaskRemoved(taskId)) {
            this.clearWebsiteProgressPolling(taskId);
            return;
          }
          this.applyWebsiteProgress(url, {
            taskStatus: 'unknown',
            latestMessage: this.t('robot-service.config.websites.progress-failed')
          });
          if (taskId) {
            this.markAttachmentTaskFailed(taskId, this.t('robot-service.config.websites.progress-failed'));
          }
          this.clearWebsiteProgressPolling(taskId);
        });
      }
    });
  }

  private normalizeWebsiteProgress(data: any, taskId?: string): Partial<WebsiteItem> {
    const source = this.pickWebsiteProgressSource(data, taskId);
    const taskStatus = this.normalizeWebsiteTaskStatus(source?.status ?? source?.taskStatus);
    return {
      taskStatus,
      statusText: this.getWebsiteStatusDisplay(taskStatus).statusText,
      subStatusText: this.getWebsiteStatusDisplay(taskStatus).subStatusText,
      stage: source?.stage ?? undefined,
      latestMessage: source?.latestMessage ?? source?.latest_message ?? source?.message ?? undefined,
      progressPercent: this.normalizeProgressPercent(source?.progressPercent ?? source?.progress_percent)
    };
  }

  private applySourceProgress(task: AttachmentTask): void {
    if (task.sourceType === 'website') {
      this.applyWebsiteProgress(task.url || '', {
        taskId: task.taskId,
        taskStatus: task.status,
        stage: task.stage,
        latestMessage: task.latestMessage,
        progressPercent: task.progressPercent
      });
      return;
    }
    if (task.sourceType === 'file') {
      this.applyFileProgress(task);
      return;
    }
    if (task.sourceType === 'youtube_link') {
      this.applyYoutubeProgress(task);
      return;
    }
    if (task.sourceType === 'plain_text') {
      this.applyPlainTextProgress(task);
    }
  }

  private pickWebsiteProgressSource(data: any, taskId?: string): any {
    if (!data) return {};
    const candidates = Array.isArray(data?.tasks)
      ? data.tasks
      : Array.isArray(data?.list)
        ? data.list
        : Array.isArray(data?.records)
          ? data.records
          : [];
    if (candidates.length > 0) {
      return candidates.find((task: any) => this.extractWebsiteTaskId(task) === taskId) ?? candidates[0];
    }
    if (data?.progress) return data.progress;
    return data;
  }

  private normalizeAttachmentTaskFromProgress(data: any, taskId: string, url: string): AttachmentTask {
    const source = this.pickWebsiteProgressSource(data, taskId);
    const status = this.normalizeWebsiteTaskStatus(source?.status ?? source?.taskStatus) ?? 'unknown';
    if (!this.attachmentTasks) {
      this.attachmentTasks = [];
    }
    const existing = this.attachmentTasks.find(task => task.taskId === taskId);
    const progressPercent = this.getAttachmentTaskPercentFromSource(source, status);
    return {
      ...existing,
      taskId,
      sourceType: this.normalizeAttachmentSourceType(source?.source_type ?? source?.sourceType) ?? existing?.sourceType ?? 'website',
      status,
      stage: source?.stage ?? existing?.stage ?? (status === 'queued' ? 'queued' : undefined),
      url: existing?.url ?? url,
      latestMessage: source?.latest_message ?? source?.latestMessage ?? source?.message ?? existing?.latestMessage,
      progressPercent,
      totalCount: this.normalizeOptionalNumber(source?.total_count ?? source?.totalCount ?? source?.total_items ?? existing?.totalCount),
      processedCount: this.normalizeOptionalNumber(
        source?.processed_count ?? source?.processedCount ?? source?.completed_items ?? existing?.processedCount
      ),
      successCount: this.normalizeOptionalNumber(
        source?.success_count ?? source?.successCount ?? source?.success_items ?? existing?.successCount
      ),
      failedCount: this.normalizeOptionalNumber(
        source?.failed_count ?? source?.failedCount ?? source?.failed_items ?? existing?.failedCount
      ),
      skippedCount: this.normalizeOptionalNumber(
        source?.skipped_count ?? source?.skippedCount ?? source?.skipped_items ?? existing?.skippedCount
      ),
      warningCount: this.normalizeOptionalNumber(source?.warning_count ?? source?.warningCount ?? existing?.warningCount),
      lastError: source?.last_error ?? source?.lastError ?? existing?.lastError,
      lookupFailures: 0,
      retainUntil: this.isAttachmentTaskTerminal(status) ? Date.now() + this.attachmentTaskRetentionMs : existing?.retainUntil
    };
  }

  private getAttachmentTaskPercentFromSource(source: any, status: WebsiteTaskStatus): number | undefined {
    if (status === 'completed' || status === 'completed_with_warnings') return 100;
    const explicitPercent = this.normalizeProgressPercent(source?.progressPercent ?? source?.progress_percent);
    if (explicitPercent !== undefined) return explicitPercent;
    const total = this.normalizeOptionalNumber(source?.total_count ?? source?.totalCount ?? source?.total_items);
    const processed = this.normalizeOptionalNumber(source?.processed_count ?? source?.processedCount ?? source?.completed_items);
    if (total !== undefined && total > 0 && processed !== undefined) {
      return Math.max(0, Math.min(99, Math.round((processed / total) * 100)));
    }
    return undefined;
  }

  private normalizeAttachmentSourceType(value: any): AttachmentSourceType | undefined {
    const sourceType = String(value ?? '').trim();
    if (sourceType === 'website' || sourceType === 'file' || sourceType === 'youtube_link' || sourceType === 'plain_text')
      return sourceType;
    if (sourceType === 'youtube' || sourceType === 'youtubeLink') return 'youtube_link';
    if (sourceType === 'text' || sourceType === 'plainText') return 'plain_text';
    return undefined;
  }

  private normalizeOptionalNumber(value: any): number | undefined {
    const normalized = Number(value);
    return Number.isFinite(normalized) ? normalized : undefined;
  }

  private upsertAttachmentTask(task: AttachmentTask): void {
    if (!this.attachmentTasks) {
      this.attachmentTasks = [];
    }
    this.ensureRemovedAttachmentTaskIds();
    this.removedAttachmentTaskIds.delete(task.taskId);
    const index = this.attachmentTasks.findIndex(item => item.taskId === task.taskId);
    if (index >= 0) {
      this.attachmentTasks[index] = { ...this.attachmentTasks[index], ...task };
    } else {
      this.attachmentTasks = [...this.attachmentTasks, task];
    }
    this.pruneRetainedAttachmentTasks();
    this.persistAttachmentTasks();
    this.mergeActiveAttachmentTasksIntoSourceLists();
  }

  private applyAttachmentTaskToVisibleList(task: AttachmentTask): void {
    if (task.sourceType === 'file') {
      this.applyFileTaskToVisibleList(task);
      return;
    }
    if (task.sourceType === 'youtube_link') {
      this.applyYoutubeTaskToVisibleList(task);
      return;
    }
    if (task.sourceType === 'website') {
      this.applyWebsiteTaskToVisibleList(task);
      return;
    }
    if (task.sourceType === 'plain_text') {
      this.applyPlainTextTaskToVisibleList(task);
    }
  }

  private getTaskDisplayPatch(task: AttachmentTask): AssetTaskDisplay {
    return {
      taskId: task.taskId,
      taskStatus: task.status,
      stage: task.stage,
      latestMessage: task.latestMessage,
      progressPercent: task.progressPercent
    };
  }

  private applyFileTaskToVisibleList(task: AttachmentTask): void {
    if (!this.uploadedFiles) {
      this.uploadedFiles = [];
    }
    const index = this.findFileTaskListIndex(task);
    const patch = this.getTaskDisplayPatch(task);
    if (index >= 0) {
      this.uploadedFiles = this.uploadedFiles.map((file, fileIndex) => (fileIndex === index ? { ...file, ...patch } : file));
      return;
    }
    this.uploadedFiles = [
      ...this.uploadedFiles,
      {
        name: task.url || this.t('robot-service.config.task.source.file'),
        size: 0,
        uploadedAt: new Date(),
        ...patch
      }
    ];
  }

  private applyYoutubeTaskToVisibleList(task: AttachmentTask): void {
    if (!this.youtubeContents) {
      this.youtubeContents = [];
    }
    const index = this.findYoutubeTaskListIndex(task);
    const patch = this.getTaskDisplayPatch(task);
    if (index >= 0) {
      this.youtubeContents = this.youtubeContents.map((youtube, youtubeIndex) =>
        youtubeIndex === index ? { ...youtube, ...patch } : youtube
      );
      return;
    }
    this.youtubeContents = [
      ...this.youtubeContents,
      {
        youtubeLink: task.url || '',
        remark: '',
        thumbnailUrl: undefined,
        ...patch
      }
    ];
  }

  private applyPlainTextTaskToVisibleList(task: AttachmentTask): void {
    if (!this.plainTextContents) {
      this.plainTextContents = [];
    }
    const index = this.findPlainTextTaskListIndex(task);
    const patch = this.getTaskDisplayPatch(task);
    if (index >= 0) {
      this.plainTextContents = this.plainTextContents.map((text, textIndex) => (textIndex === index ? { ...text, ...patch } : text));
      return;
    }
    this.plainTextContents = [
      ...this.plainTextContents,
      {
        plainText: task.url || '',
        remark: '',
        ...patch
      }
    ];
  }

  private applyWebsiteTaskToVisibleList(task: AttachmentTask): void {
    if (!task.url) return;
    this.applyWebsiteProgress(task.url, {
      taskId: task.taskId,
      taskStatus: task.status,
      stage: task.stage,
      latestMessage: task.latestMessage,
      progressPercent: task.progressPercent
    });
  }

  private markAttachmentTaskFailed(taskId: string, message: string): void {
    const existing = this.attachmentTasks.find(task => task.taskId === taskId);
    if (!existing) return;
    this.upsertAttachmentTask({
      ...existing,
      status: 'failed',
      latestMessage: message,
      lastError: message,
      retainUntil: Date.now() + this.attachmentTaskRetentionMs
    });
  }

  private pruneRetainedAttachmentTasks(): void {
    if (!this.attachmentTasks) {
      this.attachmentTasks = [];
    }
    const now = Date.now();
    this.attachmentTasks = this.attachmentTasks.filter(task => !task.retainUntil || task.retainUntil > now);
  }

  private getAttachmentTaskStorageKey(): string | null {
    if (!this.robotId || !this.serviceNumberId) return null;
    return `robot-service-config:attachment-tasks:${this.serviceNumberId}:${this.robotId}`;
  }

  private persistAttachmentTasks(): void {
    const key = this.getAttachmentTaskStorageKey();
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify({ tasks: this.attachmentTasks || [] }));
    } catch {}
  }

  private restoreAttachmentTasks(): void {
    const key = this.getAttachmentTaskStorageKey();
    if (!key) return;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const tasks = Array.isArray(parsed?.tasks) ? parsed.tasks : [];
      const now = Date.now();
      this.ensureRemovedAttachmentTaskIds();
      this.removedAttachmentTaskIds.clear();
      this.attachmentTasks = tasks.filter((task: AttachmentTask) => {
        const status = this.normalizeWebsiteTaskStatus(task?.status) ?? 'unknown';
        return !this.isAttachmentTaskTerminal(status) || !task.retainUntil || task.retainUntil > now;
      });
    } catch {
      this.attachmentTasks = [];
    }
  }

  private resumeAttachmentTaskPolling(): void {
    (this.attachmentTasks || []).forEach(task => {
      if (task.taskId && !this.isAttachmentTaskTerminal(task.status)) {
        this.pollWebsiteAssetProgress(task.taskId, task.url || '', 0);
      }
    });
  }

  private mergeActiveAttachmentTasksIntoSourceLists(): void {
    this.mergeActiveFileTasksIntoFileList();
    this.mergeActiveYoutubeTasksIntoYoutubeList();
    this.mergeActivePlainTextTasksIntoPlainTextList();
    this.mergeActiveWebsiteTasksIntoWebsiteList();
  }

  private mergeActiveFileTasksIntoFileList(): void {
    if (!this.uploadedFiles) {
      this.uploadedFiles = [];
    }
    (this.attachmentTasks || [])
      .filter(task => task.sourceType === 'file' && !this.isAttachmentTaskTerminal(task.status))
      .forEach(task => {
        const index = this.findFileTaskListIndex(task);
        const patch = {
          taskId: task.taskId,
          taskStatus: task.status,
          stage: task.stage,
          latestMessage: task.latestMessage,
          progressPercent: task.progressPercent
        };
        if (index >= 0) {
          this.uploadedFiles = this.uploadedFiles.map((file, fileIndex) => (fileIndex === index ? { ...file, ...patch } : file));
          return;
        }
        this.uploadedFiles = [
          ...this.uploadedFiles,
          {
            name: task.url || this.t('robot-service.config.task.source.file'),
            size: 0,
            uploadedAt: new Date(),
            ...patch
          }
        ];
      });
  }

  private findFileTaskListIndex(task: AttachmentTask): number {
    const taskFileName = String(task.url || '').trim();
    const taskIdIndex = this.uploadedFiles.findIndex(file => !!task.taskId && file.taskId === task.taskId);
    if (taskIdIndex >= 0) return taskIdIndex;
    if (taskFileName) {
      const nameIndex = this.uploadedFiles.findIndex(file => {
        const fileName = String(file.name || '').trim();
        return fileName === taskFileName || fileName.includes(taskFileName) || taskFileName.includes(fileName);
      });
      if (nameIndex >= 0) return nameIndex;
    }
    return -1;
  }

  private mergeActiveYoutubeTasksIntoYoutubeList(): void {
    if (!this.youtubeContents) {
      this.youtubeContents = [];
    }
    (this.attachmentTasks || [])
      .filter(task => task.sourceType === 'youtube_link' && task.url && !this.isAttachmentTaskTerminal(task.status))
      .forEach(task => {
        const index = this.findYoutubeTaskListIndex(task);
        const patch = {
          taskId: task.taskId,
          taskStatus: task.status,
          stage: task.stage,
          latestMessage: task.latestMessage,
          progressPercent: task.progressPercent
        };
        if (index >= 0) {
          this.youtubeContents = this.youtubeContents.map((youtube, youtubeIndex) =>
            youtubeIndex === index ? { ...youtube, ...patch } : youtube
          );
          return;
        }
        this.youtubeContents = [
          ...this.youtubeContents,
          {
            youtubeLink: task.url || '',
            remark: '',
            thumbnailUrl: undefined,
            ...patch
          }
        ];
      });
  }

  private findYoutubeTaskListIndex(task: AttachmentTask): number {
    const taskLink = this.normalizeYoutubeUrl(String(task.url || '').trim());
    const taskIdIndex = this.youtubeContents.findIndex(youtube => !!task.taskId && youtube.taskId === task.taskId);
    if (taskIdIndex >= 0) return taskIdIndex;
    if (taskLink) {
      const linkIndex = this.youtubeContents.findIndex(youtube => {
        const youtubeLink = this.normalizeYoutubeUrl(String(youtube.youtubeLink || '').trim());
        return youtubeLink === taskLink || youtubeLink.includes(taskLink) || taskLink.includes(youtubeLink);
      });
      if (linkIndex >= 0) return linkIndex;
    }
    if (this.youtubeContents.length === 1 && !this.youtubeContents[0].taskId) {
      return 0;
    }
    return -1;
  }

  private mergeActivePlainTextTasksIntoPlainTextList(): void {
    if (!this.plainTextContents) {
      this.plainTextContents = [];
    }
    (this.attachmentTasks || [])
      .filter(task => task.sourceType === 'plain_text' && !this.isAttachmentTaskTerminal(task.status))
      .forEach(task => this.applyPlainTextTaskToVisibleList(task));
  }

  private findPlainTextTaskListIndex(task: AttachmentTask): number {
    const taskText = String(task.url || '').trim();
    const taskIdIndex = this.plainTextContents.findIndex(text => !!task.taskId && text.taskId === task.taskId);
    if (taskIdIndex >= 0) return taskIdIndex;
    if (taskText) {
      const textIndex = this.plainTextContents.findIndex(text => {
        const plainText = String(text.plainText || '').trim();
        return plainText === taskText || plainText.includes(taskText) || taskText.includes(plainText);
      });
      if (textIndex >= 0) return textIndex;
    }
    if (this.plainTextContents.length === 1 && !this.plainTextContents[0].taskId) {
      return 0;
    }
    return -1;
  }

  private mergeActiveWebsiteTasksIntoWebsiteList(): void {
    if (!this.websites) {
      this.websites = [];
    }
    (this.attachmentTasks || [])
      .filter(task => task.sourceType === 'website' && task.url && !this.isAttachmentTaskTerminal(task.status))
      .forEach(task => {
        const index = this.websites.findIndex(website => website.url === task.url || (!!task.taskId && website.taskId === task.taskId));
        if (index >= 0) {
          this.websites[index] = {
            ...this.websites[index],
            taskId: task.taskId,
            taskStatus: task.status,
            stage: task.stage,
            latestMessage: task.latestMessage,
            progressPercent: task.progressPercent,
            ...this.getWebsiteStatusDisplay(task.status)
          };
          return;
        }
        this.websites = [
          ...this.websites,
          {
            url: task.url || '',
            remark: '',
            taskId: task.taskId,
            taskStatus: task.status,
            stage: task.stage,
            latestMessage: task.latestMessage,
            progressPercent: task.progressPercent,
            ...this.getWebsiteStatusDisplay(task.status)
          }
        ];
      });
  }

  private isAttachmentTaskTerminal(status?: WebsiteTaskStatus): boolean {
    return status === 'completed' || status === 'completed_with_warnings' || status === 'failed';
  }

  private applyWebsiteProgress(url: string, progress: Partial<WebsiteItem>): void {
    if (!this.websites) {
      this.websites = [];
    }
    const index = this.websites.findIndex(w => w.url === url);
    if (index < 0) return;
    const taskStatus = this.normalizeWebsiteTaskStatus(progress.taskStatus);
    this.websites[index] = {
      ...this.websites[index],
      ...progress,
      taskStatus,
      ...this.getWebsiteStatusDisplay(taskStatus)
    };
  }

  private applyFileProgress(task: AttachmentTask): void {
    if (!this.uploadedFiles) {
      this.uploadedFiles = [];
    }
    const index = this.findFileTaskListIndex(task);
    const patch = this.getTaskDisplayPatch(task);
    if (index >= 0) {
      this.uploadedFiles = this.uploadedFiles.map((file, fileIndex) => (fileIndex === index ? { ...file, ...patch } : file));
      return;
    }
    this.uploadedFiles = [
      ...this.uploadedFiles,
      {
        name: task.url || this.t('robot-service.config.task.source.file'),
        size: 0,
        uploadedAt: new Date(),
        ...patch
      }
    ];
  }

  private applyYoutubeProgress(task: AttachmentTask): void {
    if (!this.youtubeContents) {
      this.youtubeContents = [];
    }
    const index = this.findYoutubeTaskListIndex(task);
    const patch = this.getTaskDisplayPatch(task);
    if (index >= 0) {
      this.youtubeContents = this.youtubeContents.map((youtube, youtubeIndex) =>
        youtubeIndex === index ? { ...youtube, ...patch } : youtube
      );
      return;
    }
    this.youtubeContents = [
      ...this.youtubeContents,
      {
        youtubeLink: task.url || '',
        remark: '',
        thumbnailUrl: undefined,
        ...patch
      }
    ];
  }

  private applyPlainTextProgress(task: AttachmentTask): void {
    this.applyPlainTextTaskToVisibleList(task);
  }

  private normalizeWebsiteTaskStatus(status: any): WebsiteTaskStatus | undefined {
    const value = String(status ?? '').trim();
    if (value === 'queued' || value === 'running' || value === 'completed' || value === 'completed_with_warnings' || value === 'failed') {
      return value;
    }
    return value ? 'unknown' : undefined;
  }

  private normalizeProgressPercent(value: any): number | undefined {
    const percent = Number(value);
    if (!Number.isFinite(percent)) return undefined;
    return Math.max(0, Math.min(100, Math.round(percent)));
  }

  getWebsiteStatusDisplay(status?: WebsiteTaskStatus): { statusText?: string; subStatusText?: string } {
    switch (status) {
      case 'queued':
      case 'running':
        return {
          statusText: 'robot-service.config.websites.status.processing',
          subStatusText: 'robot-service.config.websites.status.pending-effective'
        };
      case 'completed':
        return {
          statusText: 'robot-service.config.websites.status.effective',
          subStatusText: 'robot-service.config.websites.status.available'
        };
      case 'completed_with_warnings':
        return {
          statusText: 'robot-service.config.websites.status.effective',
          subStatusText: 'robot-service.config.websites.status.warning'
        };
      case 'failed':
        return {
          statusText: 'robot-service.config.websites.status.failed'
        };
      case 'unknown':
        return {
          statusText: 'robot-service.config.websites.status.unknown'
        };
      default:
        return {};
    }
  }

  getWebsiteStatusColor(status?: WebsiteTaskStatus): string {
    if (status === 'completed') return 'success';
    if (status === 'completed_with_warnings') return 'warning';
    if (status === 'failed') return 'error';
    if (status === 'queued' || status === 'running') return 'processing';
    return 'default';
  }

  isWebsiteProcessing(status?: WebsiteTaskStatus): boolean {
    return status === 'queued' || status === 'running';
  }

  shouldShowAssetItemProgress(item: AssetTaskDisplay): boolean {
    return (
      this.isWebsiteProcessing(item.taskStatus) || item.progressPercent !== undefined || this.isAttachmentTaskTerminal(item.taskStatus)
    );
  }

  getAssetItemProgressPercent(item: AssetTaskDisplay): number {
    if (item.taskStatus === 'completed' || item.taskStatus === 'completed_with_warnings') return 100;
    return item.progressPercent ?? 0;
  }

  shouldShowAssetItemPercent(item: AssetTaskDisplay): boolean {
    return item.progressPercent !== undefined || this.isAttachmentTaskTerminal(item.taskStatus);
  }

  getAssetItemProgressStatus(status?: WebsiteTaskStatus): 'active' | 'success' | 'exception' | 'normal' {
    return this.getAttachmentTaskProgressStatus({ status } as AttachmentTask);
  }

  shouldShowWebsiteItemProgress(item: WebsiteItem): boolean {
    return !!item.taskId && this.shouldShowAssetItemProgress(item);
  }

  getWebsiteItemProgressPercent(item: WebsiteItem): number {
    return this.getAssetItemProgressPercent(item);
  }

  shouldShowWebsiteItemPercent(item: WebsiteItem): boolean {
    return !!item.taskId && this.shouldShowAssetItemPercent(item);
  }

  shouldShowFileItemProgress(item: AssetTaskDisplay & { name?: string }): boolean {
    return this.shouldShowAssetItemProgress(this.getFileItemDisplay(item));
  }

  getFileItemProgressPercent(item: AssetTaskDisplay & { name?: string }): number {
    return this.getAssetItemProgressPercent(this.getFileItemDisplay(item));
  }

  shouldShowFileItemPercent(item: AssetTaskDisplay & { name?: string }): boolean {
    return this.shouldShowAssetItemPercent(this.getFileItemDisplay(item));
  }

  getFileItemProgressStatus(item: AssetTaskDisplay & { name?: string }): 'active' | 'success' | 'exception' | 'normal' {
    return this.getAssetItemProgressStatus(this.getFileItemDisplay(item).taskStatus);
  }

  shouldShowYoutubeItemProgress(item: AssetTaskDisplay & { youtubeLink?: string }): boolean {
    return this.shouldShowAssetItemProgress(this.getYoutubeItemDisplay(item));
  }

  getYoutubeItemProgressPercent(item: AssetTaskDisplay & { youtubeLink?: string }): number {
    return this.getAssetItemProgressPercent(this.getYoutubeItemDisplay(item));
  }

  shouldShowYoutubeItemPercent(item: AssetTaskDisplay & { youtubeLink?: string }): boolean {
    return this.shouldShowAssetItemPercent(this.getYoutubeItemDisplay(item));
  }

  getYoutubeItemProgressStatus(item: AssetTaskDisplay & { youtubeLink?: string }): 'active' | 'success' | 'exception' | 'normal' {
    return this.getAssetItemProgressStatus(this.getYoutubeItemDisplay(item).taskStatus);
  }

  shouldShowPlainTextItemProgress(item: AssetTaskDisplay & { plainText?: string }): boolean {
    return this.shouldShowAssetItemProgress(this.getPlainTextItemDisplay(item));
  }

  getPlainTextItemProgressPercent(item: AssetTaskDisplay & { plainText?: string }): number {
    return this.getAssetItemProgressPercent(this.getPlainTextItemDisplay(item));
  }

  shouldShowPlainTextItemPercent(item: AssetTaskDisplay & { plainText?: string }): boolean {
    return this.shouldShowAssetItemPercent(this.getPlainTextItemDisplay(item));
  }

  getPlainTextItemProgressStatus(item: AssetTaskDisplay & { plainText?: string }): 'active' | 'success' | 'exception' | 'normal' {
    return this.getAssetItemProgressStatus(this.getPlainTextItemDisplay(item).taskStatus);
  }

  private getFileItemDisplay(item: AssetTaskDisplay & { name?: string }): AssetTaskDisplay {
    const task = this.findActiveFileTaskForItem(item);
    return task ? { ...item, ...this.getTaskDisplayPatch(task) } : item;
  }

  private buildFileDownloadPayload(
    file: { accessPath?: string; accessUrl?: string } | null | undefined
  ): { filePath: string; serviceNumberId: string; robotId: string } | undefined {
    const accessPath = this.getNormalizedDownloadValue(file?.accessPath);
    const accessUrl = this.getNormalizedDownloadValue(file?.accessUrl);
    if (!this.hasFileDownloadSource(file)) return undefined;
    if (!this.robotId || !this.serviceNumberId) return undefined;

    return {
      filePath: accessPath || accessUrl,
      serviceNumberId: this.serviceNumberId,
      robotId: this.robotId
    };
  }

  private hasFileDownloadSource(file: { accessPath?: string; accessUrl?: string } | null | undefined): boolean {
    return !!(this.getNormalizedDownloadValue(file?.accessPath) || this.getNormalizedDownloadValue(file?.accessUrl));
  }

  private getNormalizedDownloadValue(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private getYoutubeItemDisplay(item: AssetTaskDisplay & { youtubeLink?: string }): AssetTaskDisplay {
    const task = this.findActiveYoutubeTaskForItem(item);
    return task ? { ...item, ...this.getTaskDisplayPatch(task) } : item;
  }

  private getPlainTextItemDisplay(item: AssetTaskDisplay & { plainText?: string }): AssetTaskDisplay {
    const task = this.findActivePlainTextTaskForItem(item);
    return task ? { ...item, ...this.getTaskDisplayPatch(task) } : item;
  }

  private findActiveFileTaskForItem(item: AssetTaskDisplay & { name?: string }): AttachmentTask | undefined {
    const activeTasks = (this.attachmentTasks || []).filter(
      task => task.sourceType === 'file' && !this.isAttachmentTaskTerminal(task.status)
    );
    if (!activeTasks.length) return undefined;
    if (item.taskId) {
      const task = activeTasks.find(activeTask => activeTask.taskId === item.taskId);
      if (task) return task;
    }
    const fileName = String(item.name || '').trim();
    if (fileName) {
      const task = activeTasks.find(activeTask => {
        const taskFileName = String(activeTask.url || '').trim();
        return !!taskFileName && (fileName === taskFileName || fileName.includes(taskFileName) || taskFileName.includes(fileName));
      });
      if (task) return task;
    }
    return undefined;
  }

  private findActiveYoutubeTaskForItem(item: AssetTaskDisplay & { youtubeLink?: string }): AttachmentTask | undefined {
    const activeTasks = (this.attachmentTasks || []).filter(
      task => task.sourceType === 'youtube_link' && !this.isAttachmentTaskTerminal(task.status)
    );
    if (!activeTasks.length) return undefined;
    if (item.taskId) {
      const task = activeTasks.find(activeTask => activeTask.taskId === item.taskId);
      if (task) return task;
    }
    const youtubeLink = this.normalizeYoutubeUrl(String(item.youtubeLink || '').trim());
    if (youtubeLink) {
      const task = activeTasks.find(activeTask => {
        const taskLink = this.normalizeYoutubeUrl(String(activeTask.url || '').trim());
        return !!taskLink && (youtubeLink === taskLink || youtubeLink.includes(taskLink) || taskLink.includes(youtubeLink));
      });
      if (task) return task;
    }
    return activeTasks.length === 1 && (this.youtubeContents || []).length === 1 ? activeTasks[0] : undefined;
  }

  private findActivePlainTextTaskForItem(item: AssetTaskDisplay & { plainText?: string }): AttachmentTask | undefined {
    const activeTasks = (this.attachmentTasks || []).filter(
      task => task.sourceType === 'plain_text' && !this.isAttachmentTaskTerminal(task.status)
    );
    if (!activeTasks.length) return undefined;
    if (item.taskId) {
      const task = activeTasks.find(activeTask => activeTask.taskId === item.taskId);
      if (task) return task;
    }
    const plainText = String(item.plainText || '').trim();
    if (plainText) {
      const task = activeTasks.find(activeTask => {
        const taskText = String(activeTask.url || '').trim();
        return !!taskText && (plainText === taskText || plainText.includes(taskText) || taskText.includes(plainText));
      });
      if (task) return task;
    }
    return activeTasks.length === 1 && (this.plainTextContents || []).length === 1 ? activeTasks[0] : undefined;
  }

  private isAttachmentTaskRemoved(taskId?: string): boolean {
    this.ensureRemovedAttachmentTaskIds();
    return !!taskId && this.removedAttachmentTaskIds.has(taskId);
  }

  private removeAttachmentTaskBySource(sourceType: AttachmentSourceType, matcher: (task: AttachmentTask) => boolean): void {
    this.ensureRemovedAttachmentTaskIds();
    const matchedTasks = (this.attachmentTasks || []).filter(task => task.sourceType === sourceType && matcher(task));
    matchedTasks.forEach(task => {
      this.removedAttachmentTaskIds.add(task.taskId);
      this.clearWebsiteProgressPolling(task.taskId);
      this.websiteProgressInFlightTaskIds?.delete(task.taskId);
    });
    if (!matchedTasks.length) return;
    this.attachmentTasks = (this.attachmentTasks || []).filter(
      task => !matchedTasks.some(matchedTask => matchedTask.taskId === task.taskId)
    );
    this.persistAttachmentTasks();
  }

  getAttachmentTaskStatusText(status?: WebsiteTaskStatus): string {
    switch (status) {
      case 'queued':
        return this.t('robot-service.config.task.status.queued');
      case 'running':
        return this.t('robot-service.config.task.status.running');
      case 'completed':
        return this.t('robot-service.config.task.status.completed');
      case 'completed_with_warnings':
        return this.t('robot-service.config.task.status.completed-with-warnings');
      case 'failed':
        return this.t('robot-service.config.task.status.failed');
      default:
        return this.t('robot-service.config.task.status.unknown');
    }
  }

  getAttachmentTaskProgressStatus(task: AttachmentTask): 'active' | 'success' | 'exception' | 'normal' {
    if (task.status === 'failed') return 'exception';
    if (task.status === 'completed' || task.status === 'completed_with_warnings') return 'success';
    if (task.status === 'queued' || task.status === 'running') return 'active';
    return 'normal';
  }

  private clearWebsiteProgressPolling(taskId?: string): void {
    this.ensureWebsiteProgressTimeoutMap();
    if (taskId) {
      const timeoutId = this.websiteProgressTimeoutIds.get(taskId);
      if (timeoutId) {
        clearTimeout(timeoutId);
        this.websiteProgressTimeoutIds.delete(taskId);
      }
      return;
    }
    this.websiteProgressTimeoutIds.forEach(timeoutId => clearTimeout(timeoutId));
    this.websiteProgressTimeoutIds.clear();
  }

  private ensureWebsiteProgressTimeoutMap(): void {
    if (!this.websiteProgressTimeoutIds) {
      this.websiteProgressTimeoutIds = new Map<string, any>();
    }
  }

  private ensureRemovedAttachmentTaskIds(): void {
    if (!this.removedAttachmentTaskIds) {
      this.removedAttachmentTaskIds = new Set<string>();
    }
  }

  // Websites: validate url format and open preview modal
  validateWebsite(): void {
    if (!this.robotId || !this.serviceNumberId) {
      this.message.warning(this.t('robot-service.config.error.missing-params'));
      return;
    }
    const url = (this.newWebsiteUrl || '').trim();
    if (!url) {
      this.message.warning(this.t('robot-service.config.websites.required'));
      return;
    }
    if (!this.isValidUrl(url)) {
      this.message.error(this.t('robot-service.config.websites.invalid'));
      return;
    }
    // 構建安全的 iframe 預覽 URL + 顯示校驗中狀態
    this.websitePreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.websiteValidationError = null;
    this.websiteValidationLoading = true;
    this.websiteValidationVisible = true;
    this.websitePreviewAvailable = false;
    this.websiteOgImageUrl = null;
    this.websiteOgTitle = null;
    // 若 8 秒內未完成加載，視為校驗超時
    if (this.websiteValidationTimeoutId) {
      clearTimeout(this.websiteValidationTimeoutId);
    }
    this.websiteValidationTimeoutId = setTimeout(() => {
      this.websiteValidationLoading = false;
      this.websiteValidationError = this.t('robot-service.config.websites.validation-timeout');
    }, 8000);

    // 並行嘗試以 no-cors 方式探測網站可訪問性（預覽可能受 X-Frame-Options 限制）
    this.testWebsiteReachability(url);
    // 並行抓取 OG 圖片作為回退預覽
    this.fetchWebsiteOgMetadata(url);
  }

  // Confirm in modal: proceed to save via API
  confirmAddWebsite(): void {
    if (this.websiteValidationLoading) {
      return;
    }
    if (this.websiteValidationError) {
      this.message.error(this.websiteValidationError);
      return;
    }
    this.saveWebsites();
  }

  // Close validation modal without saving
  closeWebsiteValidation(): void {
    this.websiteValidationVisible = false;
    this.websiteValidationLoading = false;
    this.websiteValidationError = null;
    this.websitePreviewAvailable = false;
    if (this.websiteValidationTimeoutId) {
      clearTimeout(this.websiteValidationTimeoutId);
      this.websiteValidationTimeoutId = null;
    }
  }

  // iframe 預覽加載完成/失敗回調
  onWebsitePreviewLoad(): void {
    if (this.websiteValidationTimeoutId) {
      clearTimeout(this.websiteValidationTimeoutId);
      this.websiteValidationTimeoutId = null;
    }
    this.websiteValidationLoading = false;
    this.websiteValidationError = null;
    this.websitePreviewAvailable = true;
  }

  onWebsitePreviewError(): void {
    if (this.websiteValidationTimeoutId) {
      clearTimeout(this.websiteValidationTimeoutId);
      this.websiteValidationTimeoutId = null;
    }
    this.websiteValidationLoading = false;
    this.websiteValidationError = this.t('robot-service.config.websites.preview-failed');
    this.websitePreviewAvailable = false;
  }

  // 抓取並解析 OG 元數據：優先 og:image / twitter:image，其次 favicon
  private async fetchWebsiteOgMetadata(url: string): Promise<void> {
    try {
      const html = await this.fetchHtmlViaProxy(url);
      if (!html) return;
      const ogImage = this.extractOgImageFromHtml(html, url);
      if (ogImage) {
        const ok = await this.probeImage(ogImage, 120);
        if (ok) {
          this.websiteOgImageUrl = ogImage;
          // 拿到 OG 圖片即可視為校驗成功視圖可展示
          this.websiteValidationLoading = false;
          this.websiteValidationError = null;
        }
      }
      const title = this.extractTitleFromHtml(html);
      if (title) this.websiteOgTitle = title;
    } catch {}
  }

  private async fetchHtmlViaProxy(url: string): Promise<string | null> {
    const proxies = [
      // 通用 CORS 代理，返回原始文本
      (u: string) => `https://cors.isomorphic-git.org/${encodeURI(u)}`,
      // 文本代理，返回頁面文本快照
      (u: string) => `https://r.jina.ai/http://${encodeURI(u.replace(/^https?:\/\//, ''))}`
    ];
    for (const build of proxies) {
      const proxyUrl = build(url);
      try {
        const res = await fetch(proxyUrl, { method: 'GET' });
        if (res.ok) {
          const text = await res.text();
          if (text && text.length > 0) return text;
        }
      } catch {}
    }
    return null;
  }

  private extractOgImageFromHtml(html: string, baseUrl: string): string | null {
    const metaRegexes = [
      /<meta[^>]+property=["']og:image["'][^>]*content=["']([^"']+)["'][^>]*>/i,
      /<meta[^>]+name=["']twitter:image["'][^>]*content=["']([^"']+)["'][^>]*>/i,
      /<link[^>]+rel=["']apple-touch-icon[^"']*["'][^>]*href=["']([^"']+)["'][^>]*>/i,
      /<link[^>]+rel=["']icon["'][^>]*href=["']([^"']+)["'][^>]*>/i
    ];
    for (const r of metaRegexes) {
      const m = html.match(r);
      if (m && m[1]) {
        const raw = m[1].trim();
        try {
          const abs = new URL(raw, baseUrl).href;
          return abs;
        } catch {
          return raw;
        }
      }
    }
    return null;
  }

  private extractTitleFromHtml(html: string): string | null {
    const m = html.match(/<title[^>]*>([^<]+)<\/title>/i);
    return m && m[1] ? m[1].trim() : null;
  }

  // 使用 no-cors 探測網站是否可達；若可達但不支持內嵌預覽，仍視為校驗成功
  private testWebsiteReachability(url: string): void {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 7000);
      fetch(url, { method: 'GET', mode: 'no-cors', signal: controller.signal })
        .then(() => {
          // 請求成功（即使為 opaque）視為可訪問
          if (this.websiteValidationTimeoutId) {
            clearTimeout(this.websiteValidationTimeoutId);
            this.websiteValidationTimeoutId = null;
          }
          this.websiteValidationLoading = false;
          this.websiteValidationError = null;
        })
        .catch(() => {
          if (this.websiteValidationTimeoutId) {
            clearTimeout(this.websiteValidationTimeoutId);
            this.websiteValidationTimeoutId = null;
          }
          this.websiteValidationLoading = false;
          this.websiteValidationError = this.t('robot-service.config.websites.modal.unreachable');
        })
        .finally(() => clearTimeout(timeoutId));
    } catch (e) {
      this.websiteValidationLoading = false;
      this.websiteValidationError = this.t('robot-service.config.websites.validation-request-failed');
    }
  }

  // Websites: delete one via DELETE API
  deleteWebsite(url: string, index?: number): void {
    if (!this.robotId || !this.serviceNumberId) {
      this.message.warning(this.t('robot-service.config.error.missing-params'));
      return;
    }
    if (!url) return;
    const body = {
      serviceNumberId: this.serviceNumberId,
      robotId: this.robotId,
      webSite: [url]
    };
    const requestKey = `assets:website:delete:${url}`;
    if (!this.beginAssetDeleteRequest(requestKey)) return;
    this.http
      .post(ConstantUtil.ADMIN_SERVICENUMBER_ROBOT_ASSETS_WEBSITE_DELETE, body)
      .pipe(finalize(() => this.endAssetDeleteRequest(requestKey)))
      .subscribe(res => {
        if (res?.success === true && (res.data === true || res.data === undefined)) {
          this.message.success(this.t('robot-service.config.delete-success'));
          const current = this.websites.find(w => w.url === url);
          this.removeAttachmentTaskBySource('website', task => task.taskId === current?.taskId || task.url === url);
          // 移除本地項
          if (typeof index === 'number') {
            this.websites.splice(index, 1);
          } else {
            this.websites = this.websites.filter(w => w.url !== url);
          }
        } else {
          this.message.error(res?.msg || this.t('robot-service.config.delete-failed'));
        }
      });
  }

  private isValidUrl(url: string): boolean {
    try {
      const u = new URL(url);
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
      return false;
    }
  }

  // Normalize YouTube URL: add protocol if missing and trim
  private normalizeYoutubeUrl(url: string): string {
    const trimmed = (url || '').trim();
    if (!trimmed) return '';
    // If missing protocol, default to https
    if (!/^https?:\/\//i.test(trimmed)) {
      return `https://${trimmed}`;
    }
    return trimmed;
  }

  // Validate YouTube link (supports youtube.com and youtu.be)
  private isValidYoutubeLink(url: string): boolean {
    try {
      const normalized = this.normalizeYoutubeUrl(url);
      const u = new URL(normalized);
      const host = u.hostname.toLowerCase();
      if (!(u.protocol === 'http:' || u.protocol === 'https:')) return false;
      if (host.includes('youtube.com') || host.includes('youtube-nocookie.com')) {
        // Accept watch, shorts, playlist or other youtube.com pages
        return true;
      }
      if (host.includes('youtu.be')) {
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  // Extract YouTube video ID from common URL formats
  private extractYoutubeVideoId(url: string): string | null {
    try {
      const u = new URL(url);
      const host = u.hostname.toLowerCase();
      if (host.includes('youtu.be')) {
        // https://youtu.be/<id>
        const path = u.pathname.replace(/^\//, '');
        return path ? path.split('/')[0] : null;
      }
      if (host.includes('youtube.com')) {
        // watch?v=ID or shorts/ID or embed/ID
        const path = u.pathname.toLowerCase();
        const v = u.searchParams.get('v');
        if (v) return v;
        const parts = path.split('/').filter(Boolean);
        const idx = parts.findIndex(p => p === 'shorts' || p === 'embed');
        if (idx !== -1 && parts[idx + 1]) return parts[idx + 1];
        // Fallback: last segment if plausible
        if (parts.length) return parts[parts.length - 1];
      }
      return null;
    } catch {
      return null;
    }
  }

  // 構建 YouTube 縮略圖候選地址（由大到小降級）
  private buildYoutubeThumbCandidates(videoId: string): string[] {
    return [
      `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`,
      `https://i.ytimg.com/vi/${videoId}/sddefault.jpg`,
      `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`,
      `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`,
      `https://i.ytimg.com/vi/${videoId}/default.jpg`
    ];
  }

  // 探測圖片是否可用（過濾 120x90 占位圖）
  private probeImage(url: string, minWidth = 120): Promise<boolean> {
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => resolve(img.naturalWidth >= minWidth);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  }

  // 加載可用的縮略圖地址
  private async loadYoutubeThumbnail(videoId: string): Promise<string | null> {
    const candidates = this.buildYoutubeThumbCandidates(videoId);
    for (const u of candidates) {
      const ok = await this.probeImage(u);
      if (ok) return u;
    }
    return null;
  }

  // YouTube: add entry locally
  addYoutubeContent(): void {
    if (!this.robotId || !this.serviceNumberId) {
      this.message.warning(this.t('robot-service.config.error.missing-params'));
      return;
    }
    if (this.youtubeContents.length >= 1) {
      this.message.warning(this.t('robot-service.config.youtube.limit-single'));
      return;
    }
    const link = (this.newYoutubeLink || '').trim();
    if (!link) {
      this.message.warning(this.t('robot-service.config.youtube.required'));
      return;
    }
    if (!this.isValidYoutubeLink(link)) {
      this.message.error(this.t('robot-service.config.youtube.invalid'));
      return;
    }
  }

  // YouTube: validate via backend (metadata only) and add if passes
  validateYoutube(): void {
    if (!this.robotId || !this.serviceNumberId) {
      this.message.warning(this.t('robot-service.config.error.missing-params'));
      return;
    }
    if (this.youtubeContents.length >= 1) {
      this.message.warning(this.t('robot-service.config.youtube.limit-single'));
      return;
    }
    const raw = (this.newYoutubeLink || '').trim();
    const link = this.normalizeYoutubeUrl(raw);
    if (!link) {
      console.log('validateYoutube: no link', this.newYoutubeLink);
      this.message.warning(this.t('robot-service.config.youtube.required'));
      return;
    }
    if (!this.isValidYoutubeLink(link)) {
      this.message.error(this.t('robot-service.config.youtube.invalid'));
      return;
    }

    // 使用規範化後的值回填輸入框，避免後續保存時不一致
    this.newYoutubeLink = link;

    this.youtubeValidationLoading = true;
    this.youtubeValidationError = null;
    this.youtubeValidationVisible = true; // 打開校驗模態框

    // 改為調用後端 API，與現有 HttpService 設計保持一致
    const body = { youtubeLink: link };
    const requestKey = 'assets:youtube:validate';
    if (!this.beginPageRequest(requestKey)) {
      this.youtubeValidationLoading = false;
      return;
    }
    this.http
      .post(ConstantUtil.ADMIN_SERVICENUMBER_ROBOT_ASSETS_YOUTUBE_VALIDATE, body)
      .pipe(finalize(() => this.endPageRequest(requestKey)))
      .subscribe({
        next: res => {
          this.youtubeValidationLoading = false;
          if (res?.success === true) {
            const data = res?.data || {};
            this.youtubeVideoTitle = data?.title ?? null;
            // 後端字段名稱兼容：durationSeconds（新）/ durationSec（舊）
            this.youtubeDurationSec =
              typeof data?.durationSeconds === 'number'
                ? data.durationSeconds
                : typeof data?.durationSec === 'number'
                  ? data.durationSec
                  : null;
            this.youtubeHasCaptions = data?.hasSubtitles === true;
            this.youtubeVideoId = typeof data?.videoId === 'string' ? data.videoId : null;

            const maxNoSubtitleSec = 40 * 60;
            if (!this.youtubeHasCaptions && (this.youtubeDurationSec || 0) > maxNoSubtitleSec) {
              const minutes = Math.round((this.youtubeDurationSec || 0) / 60);
              const errMsg = this.t('robot-service.config.youtube.duration-limit', { minutes });
              this.youtubeValidationError = errMsg;
              this.message.error(errMsg);
              return;
            }
            // 校驗通過：在模態框中展示成功視圖，等待使用者點擊「確認添加」
            this.youtubeValidationError = null;
            this.message.success(this.t('robot-service.config.youtube.validation-passed'));
            // 加載縮略圖（方案A）
            const vid = this.youtubeVideoId || this.extractYoutubeVideoId(link);
            if (vid) {
              this.youtubeThumbnailUrl = null;
              this.loadYoutubeThumbnail(vid).then(url => (this.youtubeThumbnailUrl = url));
            }
          } else {
            const errMsg = res?.msg ? String(res.msg) : this.t('robot-service.config.youtube.validation-failed');
            this.youtubeValidationError = errMsg;
            this.message.error(errMsg);
          }
        },
        error: err => {
          this.youtubeValidationLoading = false;
          const errMsg = err?.message ? String(err.message) : this.t('robot-service.config.youtube.validation-failed');
          this.youtubeValidationError = errMsg;
          this.message.error(errMsg);
        }
      });
  }

  // YouTube 校驗模態框：確認添加
  confirmAddYoutube(): void {
    if (this.youtubeValidationLoading) return;
    if (this.youtubeValidationError) {
      this.message.error(this.youtubeValidationError);
      return;
    }
    this.saveYoutubeContents();
  }

  // 關閉 YouTube 校驗模態框並重置狀態
  closeYoutubeValidation(): void {
    this.youtubeValidationVisible = false;
    this.youtubeValidationLoading = false;
    this.youtubeValidationError = null;
    // 清理暫存顯示信息
    this.youtubeVideoTitle = null;
    this.youtubeVideoId = null;
    this.youtubeDurationSec = null;
    this.youtubeHasCaptions = null;
    this.youtubeThumbnailUrl = null;
  }

  // 為列表條目異步加載縮略圖
  private async attachYoutubeThumbnails(): Promise<void> {
    const tasks = this.youtubeContents.map(async (y, i) => {
      const vid = this.extractYoutubeVideoId(y.youtubeLink);
      if (!vid) return;
      const url = await this.loadYoutubeThumbnail(vid);
      if (url) {
        (this.youtubeContents[i] as any).thumbnailUrl = url;
      }
    });
    await Promise.allSettled(tasks);
  }

  // YouTube: save via SET API
  saveYoutubeContents(): void {
    if (!this.robotId || !this.serviceNumberId) {
      this.message.warning(this.t('robot-service.config.error.missing-params'));
      return;
    }
    const link = this.normalizeYoutubeUrl((this.newYoutubeLink || '').trim());
    const remark = (this.newYoutubeRemark || '').trim();
    if (!link) {
      this.message.warning(this.t('robot-service.config.youtube.required'));
      return;
    }
    if (!this.isValidYoutubeLink(link)) {
      this.message.error(this.t('robot-service.config.youtube.invalid'));
      return;
    }
    const body = {
      serviceNumberId: this.serviceNumberId,
      robotId: this.robotId,
      // 後端僅接受單一項
      youtubeContents: [{ youtubeLink: link, remark }]
    };
    const requestKey = 'assets:youtube:set';
    if (!this.beginPageRequest(requestKey)) return;
    this.http
      .post(ConstantUtil.ADMIN_SERVICENUMBER_ROBOT_ASSETS_YOUTUBE_SET, body)
      .pipe(finalize(() => this.endPageRequest(requestKey)))
      .subscribe(res => {
        if (this.isAssetSetSuccess(res)) {
          const data = this.getAssetSetPayload(res?.data);
          const displayMessage =
            typeof data?.displayMessage === 'string' && data.displayMessage.trim()
              ? data.displayMessage
              : this.t('robot-service.config.save-success');
          this.message.success(displayMessage);
          if (this.shouldTrackAssetSetTask(data, 'youtube_link')) {
            this.trackAttachmentTaskFromSetPayload(data, 'youtube_link', link, displayMessage);
          } else {
            this.loadAssetsList();
          }
          // 保存成功後，如校驗模態框仍開啟則自動關閉
          if (this.youtubeValidationVisible) {
            this.youtubeValidationVisible = false;
          }
          this.newYoutubeLink = '';
          this.newYoutubeRemark = '';
        } else {
          this.message.error(res?.msg || this.t('robot-service.config.save-failed'));
        }
      });
  }

  // YouTube: delete one via DELETE API
  deleteYoutubeLink(link: string, index?: number): void {
    if (!this.robotId || !this.serviceNumberId) {
      this.message.warning(this.t('robot-service.config.error.missing-params'));
      return;
    }
    if (!link) return;
    const body = {
      serviceNumberId: this.serviceNumberId,
      robotId: this.robotId,
      youtubeLinks: [link]
    };
    const requestKey = `assets:youtube:delete:${link}`;
    if (!this.beginAssetDeleteRequest(requestKey)) return;
    this.http
      .post(ConstantUtil.ADMIN_SERVICENUMBER_ROBOT_ASSETS_YOUTUBE_DELETE, body)
      .pipe(finalize(() => this.endAssetDeleteRequest(requestKey)))
      .subscribe(res => {
        if (res?.success === true && (res.data === true || res.data === undefined)) {
          this.message.success(this.t('robot-service.config.delete-success'));
          const current = this.youtubeContents.find(y => y.youtubeLink === link);
          this.removeAttachmentTaskBySource('youtube_link', task => task.taskId === current?.taskId || task.url === link);
          // 移除本地項
          if (typeof index === 'number') {
            this.youtubeContents.splice(index, 1);
          } else {
            this.youtubeContents = this.youtubeContents.filter(y => y.youtubeLink !== link);
          }
        } else {
          this.message.error(res?.msg || this.t('robot-service.config.delete-failed'));
        }
      });
  }

  // PlainText: save single content via SET API
  savePlainTextContent(): void {
    if (!this.robotId || !this.serviceNumberId) {
      this.message.warning(this.t('robot-service.config.error.missing-params'));
      return;
    }
    const content = (this.newPlainTextContent || '').trim();
    if (!content) {
      this.message.warning(this.t('robot-service.config.texts.required'));
      return;
    }
    const body = {
      serviceNumberId: this.serviceNumberId,
      robotId: this.robotId,
      content
    };
    const requestKey = 'assets:text:set';
    if (!this.beginPageRequest(requestKey)) return;
    this.http
      .post(ConstantUtil.ADMIN_SERVICENUMBER_ROBOT_ASSETS_PLAIN_TEXT_SET, body)
      .pipe(finalize(() => this.endPageRequest(requestKey)))
      .subscribe(res => {
        if (this.isAssetSetSuccess(res)) {
          const data = this.getAssetSetPayload(res?.data);
          const displayMessage =
            typeof data?.displayMessage === 'string' && data.displayMessage.trim()
              ? data.displayMessage
              : this.t('robot-service.config.save-success');
          this.message.success(displayMessage);
          if (this.shouldTrackAssetSetTask(data, 'plain_text')) {
            this.trackAttachmentTaskFromSetPayload(data, 'plain_text', content, displayMessage);
          } else {
            // 刷新列表並清空輸入框
            this.loadAssetsList();
          }
          this.newPlainTextContent = '';
        } else {
          this.message.error(res?.msg || this.t('robot-service.config.save-failed'));
        }
      });
  }

  // PlainText: delete one content via DELETE API
  deletePlainTextContent(content: string, index?: number): void {
    if (!this.robotId || !this.serviceNumberId) {
      this.message.warning(this.t('robot-service.config.error.missing-params'));
      return;
    }
    if (!content) return;
    const body = {
      serviceNumberId: this.serviceNumberId,
      content: content,
      robotId: this.robotId
    };
    const requestKey = `assets:text:delete:${content}`;
    if (!this.beginAssetDeleteRequest(requestKey)) return;
    this.http
      .post(ConstantUtil.ADMIN_SERVICENUMBER_ROBOT_ASSETS_PLAIN_TEXT_DELETE, body)
      .pipe(finalize(() => this.endAssetDeleteRequest(requestKey)))
      .subscribe(res => {
        if (res?.success === true && (res.data === true || res.data === undefined)) {
          this.message.success(this.t('robot-service.config.delete-success'));
          const current = this.plainTextContents.find(p => p.plainText === content);
          this.removeAttachmentTaskBySource('plain_text', task => task.taskId === current?.taskId || task.url === content);
          if (typeof index === 'number') {
            this.plainTextContents.splice(index, 1);
          } else {
            this.plainTextContents = this.plainTextContents.filter(p => p.plainText !== content);
          }
          this.calculateKnowledgeUsage();
        } else {
          this.message.error(res?.msg || this.t('robot-service.config.delete-failed'));
        }
      });
  }

  // 計算字符串的位元組大小（預設 UTF-8）
  private getStringBytes(s: string): number {
    try {
      return new TextEncoder().encode(s || '').length;
    } catch {
      return (s || '').length;
    }
  }

  // 更新知識容量使用情況（僅包含文件與文本）
  private calculateKnowledgeUsage(): void {
    const filesBytes = (this.uploadedFiles || []).reduce((sum, f) => sum + (Number(f?.size) || 0), 0);
    const textBytes = (this.plainTextContents || []).reduce((sum, t) => sum + this.getStringBytes(t?.plainText || ''), 0);
    const used = filesBytes + textBytes;
    this.knowledgeUsedBytes = used;
    this.knowledgeUsagePercent = this.knowledgeTotalBytes > 0 ? Math.min(100, Math.round((used / this.knowledgeTotalBytes) * 100)) : 0;
  }

  formatFileSize(size: number): string {
    if (size == null) return '';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }

  async close(): Promise<void> {
    const canLeave = await this.canDeactivate();
    if (!canLeave) return;
    const tenantId =
      this.tenantIdInput ||
      this.modalData?.tenantId ||
      this.route.snapshot.paramMap.get('id') ||
      this.route.parent?.snapshot.paramMap.get('tenantId');
    const serviceNumberId =
      this.serviceNumberIdInput || this.modalData?.serviceNumberId || this.route.snapshot.queryParamMap.get('serviceNumberId');
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    if (this.embeddedInDrawer) {
      this.modalRef?.close();
      this.closeRequested.emit();
      return;
    }
    if (returnUrl) {
      this.router.navigateByUrl(returnUrl);
      return;
    }
    let tenantType: string | null = null;
    const currentTenantRaw = localStorage.getItem('current_tenant');
    if (currentTenantRaw) {
      try {
        const tenant = JSON.parse(currentTenantRaw);
        tenantType = tenant?.type ?? null;
      } catch {
        tenantType = null;
      }
    }
    if (tenantType === 'Person') {
      this.router.navigate(['personal-robot-setting', tenantId]);
      return;
    }
    if (serviceNumberId) {
      this.router.navigate(['/servicenumber/detail', serviceNumberId], {
        queryParams: { tab: 'robot' }
      });
    } else {
      this.router.navigate(['personal-robot-setting', tenantId]);
    }
  }

  // 路由離開守衛：未保存更改時提示
  canDeactivate(): boolean | Promise<boolean> {
    if (!this.configForm || !this.configForm.dirty) return true;
    const ok = window.confirm(this.i18n.fanyi('robot-service.config.unsaved-leave-confirm'));
    return Promise.resolve(ok);
  }
}
